import React, { useState, useMemo } from 'react';
import { Search, Save, CheckCircle } from 'lucide-react';
import { formatDisplayDate } from './DateFilter';
import { Pagination, SearchBox } from './TableControls';

// Item #5's exact field lists — read-only fields render as plain text no
// matter what; only these 13 keys ever get an <input>, and the backend's
// whitelist (PATCH .../kpi-fields) is the real enforcement behind this UI lock.
// const EDITABLE_FIELDS = [
//     { key: 'alternate_number', label: 'Alternate Number' },
//     { key: 'alternate_email', label: 'Alternate Email' },
//     { key: 'skill', label: 'Skill' },
//     { key: 'current_employer', label: 'Current Employer' },
//     { key: 'designation', label: 'Designation' },
//     { key: 'total_experience_years', label: 'Total Experience', type: 'number' },
//     { key: 'relevant_experience_years', label: 'Relevant Experience', type: 'number' },
//     { key: 'notice_period', label: 'Notice Period' },
//     { key: 'education', label: 'Education' },
//     { key: 'current_package', label: 'Current Package' },
//     { key: 'expected_package', label: 'Expected Package' },
//     { key: 'bill_rate', label: 'Evanti Pay / Bill Rate' },
//     { key: 'location', label: 'Location' },
//     { key: 'details_remarks', label: 'Remarks' },
//     { key: 'remark', label: 'Call Remark' }
// ];

const PAGE_SIZE = 10;
const API = process.env.REACT_APP_BACKEND_API_URL;
const inputStyle = { width: '100%', padding: '5px 7px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '12px', boxSizing: 'border-box' };
const bigInputStyle = { ...inputStyle, minWidth: '250px' };
const cellStyle = { padding: '8px', fontSize: '12px', verticalAlign: 'top' };
const readOnlyCellStyle = { ...cellStyle, color: '#475569' };

// `server` (optional): { total, page, pageSize, totalPages, setPage, setPageSize, loading, search, setSearch } — when
// given, `kpiDrafts` is already ONE page filtered/sorted by the API, so the grid neither searches nor pages locally.
// Without it the grid behaves exactly as before (client-side search, 10 per page).
const CandidateKPIDetailsGrid = ({ kpiDrafts, currentUser, onRowSaved, server }) => {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [editValues, setEditValues] = useState({});
    const [savingId, setSavingId] = useState(null);
    const [savedFlashId, setSavedFlashId] = useState(null);
    const [rowErrors, setRowErrors] = useState({});

    const filtered = useMemo(() => {
        if (server) return kpiDrafts;
        const q = search.trim().toLowerCase();
        if (!q) return kpiDrafts;
        return kpiDrafts.filter(k =>
            (k.name || '').toLowerCase().includes(q) ||
            (k.email || '').toLowerCase().includes(q) ||
            (k.number || '').toLowerCase().includes(q) ||
            (k.skill || '').toLowerCase().includes(q) ||
            (k.task_display_id || '').toLowerCase().includes(q) ||
            (k.candidate_code || '').toLowerCase().includes(q)
        );
    }, [kpiDrafts, search, server]);

    const totalPages = server ? server.totalPages : Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const pageRows = server ? filtered : filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const rowOffset = server ? (server.page - 1) * server.pageSize : (page - 1) * PAGE_SIZE;

    const getFieldValue = (row, field) => {
        if (row.id in editValues && field in editValues[row.id]) return editValues[row.id][field];
        return row[field] ?? '';
    };

    const handleFieldChange = (rowId, field, value) => {
        setEditValues(prev => ({ ...prev, [rowId]: { ...prev[rowId], [field]: value } }));
    };

    const handleSaveRow = async (row) => {
        const changes = editValues[row.id];
        if (!changes || Object.keys(changes).length === 0) return;

        setSavingId(row.id);
        setRowErrors(prev => ({ ...prev, [row.id]: null }));
        try {
            const response = await fetch(
                `${API}/api/tasks/${row.task_id}/candidates/${row.id}/kpi-fields`,
                {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        ...changes,
                        requester_name: currentUser?.name,
                        requester_role: currentUser?.role
                    })
                }
            );
            const data = await response.json();
            if (!response.ok) {
                setRowErrors(prev => ({ ...prev, [row.id]: data.error || 'Failed to save.' }));
                return;
            }
            onRowSaved?.(row.id, data);
            setEditValues(prev => {
                const next = { ...prev };
                delete next[row.id];
                return next;
            });
            setSavedFlashId(row.id);
            setTimeout(() => setSavedFlashId(id => (id === row.id ? null : id)), 2000);
        } catch (err) {
            console.error('Failed to save KPI row:', err);
            setRowErrors(prev => ({ ...prev, [row.id]: 'Server connection failed.' }));
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: '#1f2937' }}>
                    Candidate KPI Details ({server ? server.total.toLocaleString() : filtered.length})
                </h3>
                {server ? (
                    <SearchBox value={server.search} onChange={server.setSearch} placeholder="Search name, email, number, skill, task ID..." minWidth="260px" />
                ) : (
                    <div style={{ position: 'relative' }}>
                        <Search size={13} style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                        <input
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            placeholder="Search name, email, number, skill, task ID..."
                            style={{ padding: '7px 10px 7px 28px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '12px', minWidth: '260px' }}
                        />
                    </div>
                )}
            </div>

            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                        <tr style={{ textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ padding: '8px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>S.No</th>
                            {['Candidate ID', 'Candidate Name', 'Email', 'Mobile Number', 'Alternate Number', 'Alternate Email',
                                'Job ID', 'Req ID', 'Job Role', 'Company', 'Client SPOC', 'Evanti SPOC',
                                'Skill', 'Current Employer', 'Designation', 'Total Experience', 'Relevant Experience', 'Notice Period', 'Education',
                                'Current Package', 'Expected Package', 'Evanti Pay / Bill Rate',
                                'Candidate Budget', 'Client Budget',
                                'Location', 'Stage', 'Call Remark', 'Remarks', 'Created Date', 'Updated Date', 'Save'].map(h => (
                                    <th key={h} style={{ padding: '8px', color: '#64748b', fontWeight: '700', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((row, snoIdx) => {
                            const hasChanges = !!editValues[row.id] && Object.keys(editValues[row.id]).length > 0;
                            return (
                                <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9', background: savedFlashId === row.id ? '#f0fdf4' : 'transparent' }}>
                                    <td style={{ ...readOnlyCellStyle, fontWeight: '600', color: '#64748b' }}>{rowOffset + snoIdx + 1}</td>
                                    <td style={{ ...readOnlyCellStyle, fontWeight: '700', color: '#7c3aed' }}>{row.candidate_code || '-'}</td>
                                    <td style={readOnlyCellStyle}>{row.name || '-'}</td>
                                    <td style={readOnlyCellStyle}>{row.email || '-'}</td>
                                    <td style={readOnlyCellStyle}>{row.number || '-'}</td>

                                    <td style={cellStyle}><input style={inputStyle} value={getFieldValue(row, 'alternate_number')} onChange={(e) => handleFieldChange(row.id, 'alternate_number', e.target.value)} /></td>
                                    <td style={cellStyle}><input style={inputStyle} value={getFieldValue(row, 'alternate_email')} onChange={(e) => handleFieldChange(row.id, 'alternate_email', e.target.value)} /></td>

                                    <td style={readOnlyCellStyle}>{row.job_id || '-'}</td>
                                    <td style={readOnlyCellStyle}>{row.req_id || '-'}</td>
                                    <td style={readOnlyCellStyle}>{row.job_role || 'N/A'}</td>
                                    <td style={readOnlyCellStyle}>{row.company || '-'}</td>
                                    <td style={readOnlyCellStyle}>{row.client_spoc || '-'}</td>
                                    <td style={readOnlyCellStyle}>{row.evanti_spoc || '-'}</td>

                                    <td style={cellStyle}><input style={bigInputStyle} value={getFieldValue(row, 'skill')} onChange={(e) => handleFieldChange(row.id, 'skill', e.target.value)} /></td>
                                    <td style={cellStyle}><input style={bigInputStyle} value={getFieldValue(row, 'current_employer')} onChange={(e) => handleFieldChange(row.id, 'current_employer', e.target.value)} /></td>
                                    <td style={cellStyle}><input style={bigInputStyle} value={getFieldValue(row, 'designation')} onChange={(e) => handleFieldChange(row.id, 'designation', e.target.value)} /></td>
                                    <td style={cellStyle}><input type="number" style={inputStyle} value={getFieldValue(row, 'total_experience_years')} onChange={(e) => handleFieldChange(row.id, 'total_experience_years', e.target.value)} /></td>
                                    <td style={cellStyle}><input type="number" style={inputStyle} value={getFieldValue(row, 'relevant_experience_years')} onChange={(e) => handleFieldChange(row.id, 'relevant_experience_years', e.target.value)} /></td>
                                    <td style={cellStyle}><input style={inputStyle} value={getFieldValue(row, 'notice_period')} onChange={(e) => handleFieldChange(row.id, 'notice_period', e.target.value)} /></td>
                                    <td style={cellStyle}><input style={inputStyle} value={getFieldValue(row, 'education')} onChange={(e) => handleFieldChange(row.id, 'education', e.target.value)} /></td>

                                    {/* <td style={readOnlyCellStyle}>{row.vendor_name || '-'}</td> */}

                                    <td style={cellStyle}><input style={inputStyle} value={getFieldValue(row, 'current_package')} onChange={(e) => handleFieldChange(row.id, 'current_package', e.target.value)} /></td>
                                    <td style={cellStyle}><input style={inputStyle} value={getFieldValue(row, 'expected_package')} onChange={(e) => handleFieldChange(row.id, 'expected_package', e.target.value)} /></td>
                                    <td style={cellStyle}><input style={inputStyle} value={getFieldValue(row, 'bill_rate')} onChange={(e) => handleFieldChange(row.id, 'bill_rate', e.target.value)} /></td>

                                    <td style={readOnlyCellStyle} title="From the linked job requirement">{row.candidate_budget || '-'}</td>
                                    <td style={readOnlyCellStyle} title="From the linked job requirement">{row.client_budget || '-'}</td>

                                    <td style={cellStyle}><input style={bigInputStyle} value={getFieldValue(row, 'location')} onChange={(e) => handleFieldChange(row.id, 'location', e.target.value)} /></td>

                                    <td style={readOnlyCellStyle} title="Edit from the Interviews tab">{row.stage_remark || '-'}</td>

                                    <td style={readOnlyCellStyle}>{row.remark || '-'}</td>
                                    <td style={readOnlyCellStyle}><input style={bigInputStyle} value={getFieldValue(row, 'details_remarks')} onChange={(e) => handleFieldChange(row.id, 'details_remarks', e.target.value)} /></td>

                                    {/* <td style={{ ...readOnlyCellStyle, fontWeight: '700', color: '#2563eb' }}>{row.task_display_id || '-'}</td> */}
                                    {/* <td style={readOnlyCellStyle}>{row.assigned_to || '-'}</td> */}
                                    <td style={readOnlyCellStyle}>{formatDisplayDate(row.created_at)}</td>
                                    <td style={readOnlyCellStyle}>{formatDisplayDate(row.updated_at)}</td>

                                    <td style={cellStyle}>
                                        <button
                                            onClick={() => handleSaveRow(row)}
                                            disabled={!hasChanges || savingId === row.id}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '4px', border: 'none', borderRadius: '4px',
                                                padding: '5px 9px', fontSize: '11px', fontWeight: '700',
                                                background: savedFlashId === row.id ? '#dcfce7' : hasChanges ? '#2563eb' : '#e2e8f0',
                                                color: savedFlashId === row.id ? '#166534' : hasChanges ? '#fff' : '#94a3b8',
                                                cursor: hasChanges && savingId !== row.id ? 'pointer' : 'not-allowed', whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {savingId === row.id ? '...' : savedFlashId === row.id ? <><CheckCircle size={12} /> Saved</> : <><Save size={12} /> Save</>}
                                        </button>
                                        {rowErrors[row.id] && (
                                            <div style={{ color: '#dc2626', fontSize: '10px', marginTop: '4px', maxWidth: '140px' }}>{rowErrors[row.id]}</div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {pageRows.length === 0 && (
                            <tr><td colSpan={34} style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No candidate KPI records found.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {server && (
                <Pagination page={server.page} totalPages={server.totalPages} total={server.total} pageSize={server.pageSize}
                    onPageChange={server.setPage} onPageSizeChange={server.setPageSize} loading={server.loading} label="candidates" />
            )}
            {!server && totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '14px' }}>
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}
                    >
                        Previous
                    </button>
                    <span style={{ fontSize: '12px', color: '#475569' }}>Page {page} of {totalPages}</span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.5 : 1 }}
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default CandidateKPIDetailsGrid;